import mapboxgl from 'mapbox-gl'

export class MapboxMapManager {
  private map: mapboxgl.Map | null = null
  private container: HTMLDivElement | null = null
  private currentStyle: string = 'mapbox://styles/mapbox/satellite-streets-v12'
  private options: { disableNavigation?: boolean; embedMode?: boolean }

  constructor(container: HTMLDivElement, options?: { disableNavigation?: boolean; embedMode?: boolean }) {
    this.container = container
    this.options = options || {}
    this.initializeMap()
  }

  private initializeMap() {
    if (!this.container) return

    // Set Mapbox access token
    const env = import.meta.env as any
    mapboxgl.accessToken = env?.VITE_MAPBOX_ACCESS_TOKEN
    
    if (!mapboxgl.accessToken) {
      throw new Error('VITE_MAPBOX_ACCESS_TOKEN environment variable is required')
    }

    this.map = new mapboxgl.Map({
      container: this.container,
      style: this.currentStyle,
      center: [144.9631, -37.8136], // Melbourne
      zoom: 10,
      pitch: 0,
      bearing: 0
    })

    // Add navigation controls only if not disabled
    if (!this.options.disableNavigation) {
      this.map.addControl(new mapboxgl.NavigationControl(), 'top-right')
    }

    // Add terrain control for 3D elevation (if available)
    if ((mapboxgl as any).TerrainControl) {
      this.map.addControl(
        new (mapboxgl as any).TerrainControl({
          source: 'mapbox-dem',
          exaggeration: 1.5
        }),
        'top-right'
      )
    }

    // Apply the SAME styles to ALL maps - matching HomePage
    const style = document.createElement('style')
    style.textContent = `
      /* Dark mode styling for Mapbox navigation controls - SAME FOR ALL MAPS */
      .mapboxgl-ctrl-group {
        background-color: rgba(31, 41, 55, 0.6) !important; /* gray-800/60 */
        backdrop-filter: blur(4px) !important;
        -webkit-backdrop-filter: blur(4px) !important;
        border: none !important;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
      }
      
      .mapboxgl-ctrl-group button {
        background-color: transparent !important;
        border-color: rgba(55, 65, 81, 0.3) !important; /* gray-700 with lower opacity */
        width: 32px !important;
        height: 32px !important;
      }
      
      .mapboxgl-ctrl-group button:hover {
        background-color: rgba(31, 41, 55, 0.8) !important; /* gray-800/80 on hover */
      }
      
      .mapboxgl-ctrl-group button:focus {
        box-shadow: none !important;
        outline: none !important;
      }
      
      /* Icon colors */
      .mapboxgl-ctrl-icon {
        filter: invert(1) !important; /* Make icons white */
      }
      
      /* Compass specific styling */
      .mapboxgl-ctrl-compass .mapboxgl-ctrl-icon {
        background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg width='29' height='29' viewBox='0 0 29 29' xmlns='http://www.w3.org/2000/svg' fill='%23ffffff'%3E%3Cpath d='M10.5 14l4-8 4 8z'/%3E%3Cpath d='M10.5 16l4 8 4-8z' fill='%23ccc'/%3E%3C/svg%3E") !important;
        filter: none !important;
      }
    `
    
    style.textContent += `
      /* Position navigation controls below header on mobile, normal on desktop */
      .mapboxgl-ctrl-top-right {
        top: 10px !important; /* Default position */
      }
      
      @media (max-width: 768px) {
        .mapboxgl-ctrl-top-right {
          top: 74px !important; /* 64px header + 10px spacing on mobile */
        }
        
        /* When no header, reset to normal position */
        .route-display-map .mapboxgl-ctrl-top-right {
          top: 10px !important;
        }
        
        /* When header is present (parent has pt-16 class) */
        .pt-16 .route-display-map .mapboxgl-ctrl-top-right {
          top: 74px !important;
        }
      }
    `
    this.container?.appendChild(style)

    // Add terrain source
    this.map.on('style.load', () => {
      this.addTerrainSource()
    })
  }

  private addTerrainSource() {
    if (!this.map || this.map.getSource('mapbox-dem')) return

    this.map.addSource('mapbox-dem', {
      type: 'raster-dem',
      url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
      tileSize: 512,
      maxzoom: 14
    })

    // Enable 3D terrain
    this.map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
  }

  public getMap(): mapboxgl.Map | null {
    return this.map
  }

  public isLoaded(): boolean {
    return this.map?.loaded() || false
  }

  public on(event: string, callback: () => void) {
    this.map?.on(event, callback)
  }

  public fitBounds(bounds: mapboxgl.LngLatBoundsLike, options?: mapboxgl.FitBoundsOptions) {
    this.map?.fitBounds(bounds, {
      padding: 50,
      duration: 1000,
      ...options
    })
  }

  public addSource(id: string, source: mapboxgl.AnySourceData) {
    if (!this.map?.getSource(id)) {
      this.map?.addSource(id, source)
    }
  }

  public addLayer(layer: mapboxgl.AnyLayer) {
    if (!this.map?.getLayer(layer.id)) {
      this.map?.addLayer(layer)
    }
  }

  public removeLayer(id: string) {
    if (this.map?.getLayer(id)) {
      this.map.removeLayer(id)
    }
  }

  public removeSource(id: string) {
    if (this.map?.getSource(id)) {
      this.map.removeSource(id)
    }
  }

  public setStyle(styleUrl: string) {
    if (this.map && styleUrl !== this.currentStyle) {
      this.currentStyle = styleUrl
      this.map.setStyle(styleUrl)
      
      // Re-add terrain source after style loads
      this.map.once('style.load', () => {
        this.addTerrainSource()
      })
    }
  }

  public getCurrentStyle(): string {
    return this.currentStyle
  }

  public flyTo(center: [number, number], options?: mapboxgl.FlyToOptions) {
    this.map?.flyTo({
      center,
      zoom: options?.zoom || 16,
      duration: options?.duration || 1500,
      curve: options?.curve || 1.2,
      ...options
    })
  }

  public destroy() {
    if (!this.map) return

    try {
      // Remove event listeners that might still be attached
      this.map.off('style.load')
      this.map.off('load')
      this.map.off('error')
      
      // Remove terrain if it exists
      try {
        this.map.setTerrain(null)
      } catch (error) {
        // Ignore terrain removal errors
      }
      
      // Remove DEM source if it exists
      try {
        if (this.map.getSource('mapbox-dem')) {
          this.map.removeSource('mapbox-dem')
        }
      } catch (error) {
        // Ignore source removal errors
      }
      
      // Finally remove the map
      this.map.remove()
    } catch (error) {
      console.warn('Error during map cleanup:', error)
      // Force remove even if cleanup fails
      try {
        this.map.remove()
      } catch (removeError) {
        // Ignore removal errors
      }
    } finally {
      this.map = null
      this.container = null
    }
  }
}